import { useState } from "react";
import weightLoss from "@/assets/images/howDoesItWork/weightLoss.png";
import weightLossMobile from "@/assets/images/howDoesItWork/weightLossMobile.png";
import notes from "@/assets/images/howDoesItWork/notes.svg?react";
import clock from "@/assets/images/howDoesItWork/clock.svg?react";
import box from "@/assets/images/howDoesItWork/box.svg?react";
import insulin from "@/assets/images/howDoesItWork/insulin.png";
import appetite from "@/assets/images/howDoesItWork/appetite.png";
import gastric from "@/assets/images/howDoesItWork/gastric.png";
import doctor from "@/assets/images/howDoesItWork/doctor.png";
import Modal from "@/components/HelpModal/Modal";

const CONTENT = {
  howDoesItWork: {
    kicker: "Weight loss, Explained",
    title: "How does GLP-1 work?",
    intro:
      "Semaglutide works by attaching to the same receptor spots as GLP-1 (a natural hormone), but it doesn't need food to trigger it. It essentially tricks your brain into thinking you're full, so you eat less without feeling hungry.",
    benefits: [
      {
        title: "Suppression of appetite",
        text:
          "Semaglutide slows the release of sugar into your bloodstream, reducing energy crashes that often lead to cravings and snacking.",
        icon: appetite,
      },
      {
        title: "Delayed gastric emptying",
        text:
          "Semaglutide helps you feel full longer after eating, so you're not constantly thinking about your next meal.",
        icon: gastric,
      },
      {
        title: "Promotion of insulin production",
        text:
          "Semaglutide activates insulin secretion in the pancreas, regulating blood sugar levels and turning on your metabolic processes.",
        icon: insulin,
      }
    ],
  },
  cta: "Continue with quiz",
  quote: {
    title: "Weight loss is complex,\nespecially when your body is working against you.",
    text:
      "Hormone imbalances, a sluggish metabolism, and blood sugar fluctuations can make weight loss harder, even when you’re doing everything right. Our science-backed GLP-1 medications help regulate appetite, improve blood sugar control, and support sustainable change. It’s designed to work with your biology, not against it.",
    image: doctor,
    author: "Sajad Zalzala, M.D., SkinnyRx Medical Director",
  },
  faqs: [
    {
      title: "What happens after I sign up?",
      text: `Once you complete the health intake quiz, a licensed clinician will review your information. Depending on your health needs, you may either have a video call with a doctor or receive approval for a one-time medication kit within 24 hours.
      After your order is reviewed and approved by a provider, your medication will be processed, and you will receive shipping and tracking information within 2 business days. You'll have unlimited access to our patient care team and providers for any questions.
      You'll also get text and email updates with tracking information for your shipment.
      If you have questions about progress, side effects, or dosage, you can consult with a clinician at no extra cost through our patient portal.`
    },
    {
      title: "How long do I need to stay on medication?",
      text: `Medication is a long–term use weight loss treatment not meant for short–term use.
      When you stop taking it, the triggers that previously drove unhealthy eating behaviors may return.
      Always speak with a licensed medical provider before stopping treatment.`
    },
    {
      title: "What's the difference between injectable vs sublingual?",
      text: `Injectable medications are administered directly into the body through a needle. This method provides very direct delivery into the bloodstream or tissues and is administered once per week.
      Sublingual (oral) medications are taken by placing them under the tongue, where they dissolve and are absorbed through the mucous membranes into nearby blood vessels. It is administered once per day.
      Studies have shown injectable medications to be more effective for weight loss.`
    },
    {
      title: "What is your cancellation & refund policy?",
      text: `You have the flexibility to cancel your order and receive a full refund at any point before it is approved by a licensed provider.
      After approval, your medication will be prepared and shipped.
      Please note that refund amounts for canceled services may vary depending on the time of cancellation and any promotional discounts that were originally applied.
      This ensures fair pricing relative to the actual duration of service usage.
      By using our services, you acknowledge and accept these terms.`
    },
    {
      title: "What if I'm pregnant & breastfeeding, or planning to get pregnant?",
      text: `We do not recommend anyone planning to get pregnant or already pregnant to take GLP-1 medication.
      It's always important to discuss any and all of the medications you're taking or planning to take with your provider to ensure safety.
      Consult with your doctor before starting or stopping any medication.`
    },
    {
      title: "Will I get charged if I don't qualify for medication?",
      text: `You won't be charged if you're not eligible.
      Payment is processed only after you finish the pre-qualification quiz and choose your medication.
      If the provider decides you don't qualify, a full refund will be issued within 24-48 hours.
      For video appointments, if you're deemed ineligible, there's no charge for the appointment, and you'll receive a complete refund.`
    },
  ],
  bottom: {
    title: "Expert Care, Personalized for Your Body",
    bullets: [
      {
        text: "Holistic program with guidance for medication, nutrition, movement, and sleep",
        icon: <img src={notes as unknown as string} alt="notes" className="h-6 w-6" />,
      },
      {
        text: "24/7 access to medical providers and our community support group",
        icon: <img src={clock as unknown as string} alt="clock" className="h-5 w-5" />,
      },
      {
        text: "Fast and free shipping, right to your door",
        icon: <img src={box as unknown as string} alt="box" className="h-5 w-5" />,
      }
    ]
  },
};

interface IHelpModalProps {
  open: boolean;
  onClose: () => void;
};

export const HelpModal: React.FC<IHelpModalProps> = ({
  open,
  onClose,
}) => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Modal isOpen={open} onClose={onClose}>
      <div className="relative flex flex-col gap-6 sm:gap-8 max-w-[768px] w-full mx-auto font-sofia font-normal bg-[#F8F7FE] py-6 px-4 sm:py-10 sm:px-10 rounded-3xl">
        <div className="flex flex-col gap-3">
          <p className="font-normal text-[14px] sm:text-[20px] leading-none tracking-[-0.04em] text-main-button-v2">{CONTENT.howDoesItWork.kicker}</p>
          <h2 className="text-[clamp(32px,5vw,48px)] tracking-[-0.04em] leading-none text-purple-3 font-normal font-sofia">
            {CONTENT.howDoesItWork.title}
          </h2>
          <p className="font-normal leading-[140%] tracking-[-0.04em] text-[14px] sm:text-[18px] text-purple-3 font-sofia">{CONTENT.howDoesItWork.intro}</p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 -mt-2">
          <div className="flex flex-col gap-3 sm:justify-between">
            {CONTENT.howDoesItWork.benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="flex gap-5 rounded-3xl border border-[#C3BCF226] bg-[#C3BCF233] p-3 sm:p-4 text-purple-3"
              >
                <img src={benefit.icon} alt="benefit icon" className="h-16 w-16" />
                <div className="flex flex-col gap-3">
                  <p className="text-sm sm:text-[16px] font-semibold">
                    {benefit.title}
                  </p>
                  <p className="text-[12px] sm:text-[14px] leading-[140%] tracking-[-0.04em] font-normal">{benefit.text}</p>
                </div>
              </div>
            ))}
            <button
              className="w-full h-12 rounded-3xl bg-purple10 text-[16px] leading-[150%] font-medium text-white"
              onClick={onClose}
            >
              {CONTENT.cta}
            </button>
          </div>
          <div className="w-full h-[240px] sm:h-full">
            <picture>
              <source srcSet={weightLossMobile} media="(max-width: 640px)" />
              <img
                src={weightLoss}
                alt="Weight loss"
                className="w-full h-full object-cover rounded-2xl"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </div>
        </div>
        <div className="w-full flex flex-col gap-6 py-6 px-5 rounded-3xl bg-purple-3">
          <p className="font-normal text-[20px] sm:text-[32px] leading-none text-center text-white">
            {CONTENT.bottom.title}
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {CONTENT.bottom.bullets.map((b) => (
              <div
                key={b.text}
                className="flex flex-row sm:flex-col items-center sm:items-start gap-2 sm:gap-3 p-3 sm:p-4 text-center text-white bg-white/10 rounded-xl border border-white/30"
              >
                <div className="flex justify-center shrink-0 items-center bg-white/15 h-12 w-12 rounded-full">
                  {b.icon}
                </div>
                <p className="text-[12px] sm:text-[14px] tracking-[-0.04em] leading-[120%] font-normal font-sofia text-left">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full font-normal">
          <h3 className="mb-4 sm:mb-6 text-[clamp(20px,5vw,32px)] leading-none font-normal text-purple-3">
            Frequently asked questions<br className="hidden sm:block" /> answered with precision
          </h3>
          <div className="flex flex-col gap-2 font-normal">
            {CONTENT.faqs.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={item.title}
                  className="rounded-[28px] bg-white text-purple-3 shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="flex w-full items-center justify-between px-5 sm:px-6 py-4 text-left"
                  >
                    <span className="text-[16px] sm:text-[18px] leading-none font-medium">
                      {item.title}
                    </span>
                    <span
                      className={`ml-4 flex items-center justify-center h-6 w-6 rounded-full bg-[#F8F7FE] text-[18px]}`}
                      aria-hidden
                    >
                      {isOpen ? (
                        <svg width="11" height="3" viewBox="0 0 11 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.69687 1.5H1.29688" stroke="#40336E" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5.50078 9.20001L5.50078 0.800012M9.70078 5.00001L1.30078 5.00001" stroke="#40336E" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      )}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-5 sm:px-6 pb-4 text-[12px] sm:text-[16px] tracking-[-0.04em] leading-[140%] text-purple-3">
                      {item.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <blockquote className="flex flex-col gap-6 md:flex-row md:items-end">
          <div className="order-2 flex flex-col gap-3 sm:gap-6 md:order-1">
            <svg width="32" height="27" viewBox="0 0 32 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.4818 3.68209L8.79805 14.2373L5.21654 15.2463C5.63179 14.4701 6.09895 13.8493 6.618 13.3836C7.13706 12.8662 7.73398 12.6075 8.40876 12.6075C9.91403 12.6075 11.2636 13.2284 12.4574 14.4701C13.7032 15.6602 14.326 17.2124 14.326 19.1269C14.326 21.1448 13.6253 22.8781 12.2238 24.3269C10.8743 25.7756 9.18735 26.5 7.16302 26.5C5.2425 26.5 3.55556 25.8015 2.10219 24.4045C0.70073 22.9557 0 21.1965 0 19.1269C0 18.299 0.155718 17.3418 0.467153 16.2552C0.830495 15.1687 1.47932 13.7458 2.41363 11.9866L8.64233 0.5L14.4818 3.68209ZM32 3.68209L26.3163 14.2373L22.8126 15.2463C23.176 14.4701 23.6172 13.8493 24.1363 13.3836C24.7072 12.8662 25.3041 12.6075 25.927 12.6075C27.4323 12.6075 28.8078 13.2284 30.0535 14.4701C31.2993 15.6602 31.9221 17.2124 31.9221 19.1269C31.9221 21.1448 31.2214 22.8781 29.8199 24.3269C28.4704 25.7756 26.7835 26.5 24.7591 26.5C22.8386 26.5 21.1517 25.8015 19.6983 24.4045C18.2968 22.9557 17.5961 21.1965 17.5961 19.1269C17.5961 18.299 17.7518 17.3418 18.0633 16.2552C18.3747 15.1687 18.9976 13.7458 19.9319 11.9866L26.2384 0.5L32 3.68209Z" fill="#40336E" />
            </svg>
            <div className="whitespace-pre-line text-[clamp(20px,5vw,32px)] tracking-[-0.04em] leading-[120%] font-extrabold text-purple-3">
              {CONTENT.quote.title}
            </div>
            <p className="text-[clamp(16px,4vw,18px)] tracking-[-0.04em] leading-[120%] text-purple-3">
              {CONTENT.quote.text}
            </p>
            <p className="hidden font-normal text-[16px] tracking-[-0.04em] leading-[110%] text-purple-3 md:block">
              {CONTENT.quote.author}
            </p>
            <div className="flex items-center mt-1 gap-3 md:hidden">
              <div className="bg-[#F0EDFF] h-10 w-10 rounded-full overflow-hidden">
                <img
                  src={CONTENT.quote.image}
                  alt="Doctor"
                  className="object-cover"
                />
              </div>
              <p className="text-[14px] leading-[110%] tracking-[-0.04em] text-purple-3">
                {CONTENT.quote.author}
              </p>
            </div>
          </div>
          <div className="order-1 hidden items-center justify-center md:flex md:order-2">
            <img
              src={CONTENT.quote.image}
              alt="Doctor"
              className="max-w-[238px]"
            />
          </div>
        </blockquote>
        <button
          className="h-12 w-full sm:w-[200px] mx-auto rounded-3xl bg-purple10 text-[16px] font-medium text-white"
          onClick={onClose}
        >
          {CONTENT.cta}
        </button>
      </div>
    </Modal>
  );
};
